/* eslint-disable camelcase */

import { apiService } from "../services/apiService";
import { Folder, FolderState, SidebarResponse } from "../types";
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";

// Initial state
const initialState: FolderState = {
  folders: [],
  loading: false,
  error: null,
};

// Define the createFolder asyncThunk with explicit types
export const createFolder = createAsyncThunk<Folder, string>(
  "folders/createFolder",
  async (folderName: string) => {
    const response = await axios.post<Folder>("https://your-api-url.com/create-folders", {
      name: folderName,
    });
    return response.data; // This will be of type Folder
  }
);

// fetch sidebar
export const fetchSidebar = createAsyncThunk<SidebarResponse, void, { rejectValue: string }>(
  "chats",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.fetchSidebar(); // Fetch data
      return response.data as SidebarResponse; // Cast response to SidebarResponse
    } catch (error) {
      return rejectWithValue("Failed to fetch sidebar data");
    }
  }
);

// Delete Chat Action
export const deleteChat = createAsyncThunk(
  "chat/deleteChat",
  async (id: number, { rejectWithValue }) => {
    try {
      await apiService.deleteChat(id); // Call the reusable service
      return `Deleted successfully`; // Return a success message
    } catch (error) {
      return rejectWithValue("Failed to delete chat");
    }
  }
);

// Delete Folder Action
export const deleteFolder = createAsyncThunk(
  "folder/pinFolder",
  async (id: number, { rejectWithValue }) => {
    try {
      await apiService.deleteFolder(id); // Call the reusable service
      return `Deleted successfully`; // Return a success message
    } catch (error) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return rejectWithValue((error as any).response?.data?.message || "Something went wrong");
    }
  }
);

// pin Chat Action
export const pinChat = createAsyncThunk<boolean, number>(
  "chat/pinChat",
  async (id, { rejectWithValue }) => {
    try {
      const resp = await apiService.pinChat(id); // Call the reusable service
      // console.log((resp.data as { pinned: boolean }).pinned);
      return (resp.data as { pinned: boolean }).pinned; // Return a success message
    } catch (error: unknown) {
      return rejectWithValue("Failed to Pin Chat");
    }
  }
);
// pin folder Action

export const pinFolder = createAsyncThunk(
  "chat/pinFolder",
  async (id: number, { rejectWithValue }) => {
    try {
      const resp = await apiService.pinFolder(id); // Call the reusable service
      return (resp.data as { pinned: boolean }).pinned; // Return a success message
    } catch (error) {
      return rejectWithValue("Failed to Pin Folder");
    }
  }
);
// Rename Chat Action
export const renameChat = createAsyncThunk<string, { folder_id: number; name: string }>(
  "chat/renameChat",
  async ({ folder_id, name }, { rejectWithValue }) => {
    try {
      await apiService.renameChat(folder_id, name); // Ensure you have the correct API method for renaming
      return `Renamed successfully`; // Return success message
    } catch (error) {
      return rejectWithValue("Failed to rename chat");
    }
  }
);

// Delete Folder Action
export const renameFolder = createAsyncThunk<string, { folder_id: number; name: string }>(
  "chat/renameFolder",
  async ({ folder_id, name }: { folder_id: number; name: string }, { rejectWithValue }) => {
    try {
      await apiService.renameFolder(folder_id, name); // Ensure you have the correct API method for renaming
      return `Renamed successfully`; // Return success message
    } catch (error) {
      return rejectWithValue("Failed to rename folder");
    }
  }
);

const folderSlice = createSlice({
  name: "folders",
  initialState,
  reducers: {
    resetFolders: (state) => {
      state.folders = [];
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create Folder
      .addCase(createFolder.pending, (state) => {
        state.loading = true;
      })
      .addCase(createFolder.fulfilled, (state, action: PayloadAction<Folder>) => {
        state.folders.push(action.payload); // Add created folder to the list
        state.loading = false;
      })
      .addCase(createFolder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? "Failed to create folder";
      });
  },
});

export const { resetFolders } = folderSlice.actions;

export default folderSlice.reducer;
